import './CustomerView.scss';

import { AsyncStateNotification } from '@ui/notifications';
import { coreActions } from '@sonar-software/sonar-core';
import { createAsyncSelectors } from '@lib/selectors';
import { CustomerViewExtra } from './CustomerViewExtra';
import { CustomerViewTitle } from './CustomerViewTitle';
import { LoadingSpinner } from '@ui/LoadingSpinner';
import { ShardCatalogSearch } from '../../ShardCatalogSearch';

import { ADMIN_DASHBOARD_COPY, COPY_SPECIFIC } from '@sonar-software/sonar-ts-constants';
import { adminActions, ShardSearchEntryDetails } from '@sonar-software/sonar-admin';
import { Card, Collapse } from 'antd';
import { ConfigurationPanel, SalesforceSetupPanel, UsersPanel } from './panels';
import React, { useCallback, useEffect, useState } from 'react';
import { useAction, useSonarSelector } from '@hooks';

const defaultShardCatalogDetailSelectors = createAsyncSelectors(
	(state) => state.admin.search.shardCatalog.details.default.get
);

const getCustomerSelectors = createAsyncSelectors((state) => state.core.customers.byId.get);

const patchCustomerSelectors = createAsyncSelectors((state) => state.core.customers.byId.patch);

const postLockAllUsersSelectors = createAsyncSelectors((state) => state.admin.customers.byId.lockAllUsers.post);

export const CustomerView: React.FC = () => {
	const [ selectedSearchDetails, setSelectedSearchDetails ] =
		useState<ShardSearchEntryDetails | undefined>(undefined);

	const patchCustomerIsReceived = useSonarSelector(patchCustomerSelectors.isReceived);

	const defaultShardCatalogDetail = useSonarSelector(defaultShardCatalogDetailSelectors.data);
	const getDefaultShardCatalogDetail = useAction(adminActions.requestGetAdminSearchShardCatalogDetailsDefault);

	const customer = useSonarSelector(getCustomerSelectors.data);
	const getCustomer = useAction(coreActions.requestGetCoreCustomersById);
	const resetCustomer = useAction(coreActions.resetGetCoreCustomersById);

	const getCustomerForSearch = useCallback(
		(details: ShardSearchEntryDetails) => {
			if (details) {
				getCustomer({
					customerId: details.customerId,
					shardName: details.shardName
				});
			}
		},
		[ getCustomer ]
	);

	const handleSearchDetailsChanged = useCallback(
		(details: ShardSearchEntryDetails) => {
			resetCustomer();

			setSelectedSearchDetails(details ?? defaultShardCatalogDetail ?? undefined);

			if (details) {
				getCustomerForSearch(details);
			}
		},
		[ defaultShardCatalogDetail, getCustomerForSearch, resetCustomer ]
	);

	useEffect(() => {
		if (!defaultShardCatalogDetail) {
			getDefaultShardCatalogDetail();
		}
	}, [ getDefaultShardCatalogDetail, defaultShardCatalogDetail ]);

	useEffect(() => {
		if (!selectedSearchDetails && defaultShardCatalogDetail) {
			setSelectedSearchDetails(defaultShardCatalogDetail);
			getCustomer(defaultShardCatalogDetail);
		}
	}, [ defaultShardCatalogDetail, getCustomer, selectedSearchDetails ]);

	useEffect(() => {
		if (patchCustomerIsReceived && selectedSearchDetails) {
			getCustomerForSearch(selectedSearchDetails);
		}
	}, [ patchCustomerIsReceived, resetCustomer, getCustomerForSearch, selectedSearchDetails ]);

	const isLoading = !selectedSearchDetails || !customer;

	const title = <CustomerViewTitle
		customer={ customer }
		shardName={ selectedSearchDetails?.shardName } />;

	const extra = <CustomerViewExtra
		customer={ customer }
		shardName={ selectedSearchDetails?.shardName } />;

	return (
		<LoadingSpinner spinning={ isLoading }>
			<ShardCatalogSearch
				id='search-shard-catalog'
				onDetailsChanged={ handleSearchDetailsChanged } />
			<Card
				className='admin-customer-view-card'
				extra={ extra }
				title={ title }
				variant='borderless'>
				<Collapse
					defaultActiveKey={ [ '1', '2', '3' ] }
					items={ [
						{
							children: <UsersPanel searchDetails={ selectedSearchDetails } />,
							key: '1',
							label: COPY_SPECIFIC.USERS
						}, {
							children: <SalesforceSetupPanel searchDetails={ selectedSearchDetails } />,
							key: '2',
							label: COPY_SPECIFIC.SALESFORCE_SETUP
						}, {
							children: <ConfigurationPanel
								customer={ customer }
								searchDetails={ selectedSearchDetails } />,
							key: '3',
							label: COPY_SPECIFIC.CONFIGURATION
						}
					] } />
			</Card>
			<AsyncStateNotification
				errored={ ADMIN_DASHBOARD_COPY.UPDATE_CUSTOMER_FAILED }
				failed={ ADMIN_DASHBOARD_COPY.UPDATE_CUSTOMER_FAILED }
				stateSelector={ patchCustomerSelectors.state } />
			<AsyncStateNotification
				errored={ ADMIN_DASHBOARD_COPY.LOCK_ALL_USERS_FAILED }
				failed={ ADMIN_DASHBOARD_COPY.LOCK_ALL_USERS_FAILED }
				stateSelector={ postLockAllUsersSelectors.state } />
		</LoadingSpinner>
	);
};
